import React from "react";
import { useEffect, useCallback, useState } from "react";
import { Grid, Row, Col, Panel, Divider, Text, SelectPicker } from "rsuite";
import "./draggable.css";

// Define steps for dosing motor
// [1/4, 1/2, 1, 1.5, 2, 3, 4, 5] = ["50", "100", "200", "300", "400", "600", "800", "1000"]
const stepOptions = [
  "50",
  "100",
  "200",
  "300",
  "400",
  "600",
  "800",
  "1000",
].map((item) => ({
  label: item + " (" + parseInt(item) / 200 + " rot)",
  value: item,
}));

// Define rest duration
const restDurations = [
  "500",
  "1000",
  "2000",
  "3000",
  "4000",
  "5000",
  "7500",
  "10000",
  "12500",
  "15000",
  "20000",
  "30000",
].map((item) => ({
  label: parseInt(item) / 1000 + " s",
  value: item,
}));

// Define processing frame interval
const frameIntervals = ["1", "2", "3", "4", "5"].map((item) => ({
  label: item + " s",
  value: item,
}));

function Settings() {
  const [motorStep, setMotorStep] = useState(null);
  const [restDuration, setRestDuration] = useState(null);
  const [frameInterval, setFrameInterval] = useState(null);

  const updateCosmosMotorStepValue = useCallback(async (value) => {
    const container = "settings";
    const item_id = "TSDP_T01_UNIT";
    const pk = "TSDP_T01";
    const path = "arduinoPulseStep";
    const valueType = "int";

    const apiUrl = "/api/patchItemCosmos";
    const params = new URLSearchParams({
      container,
      item_id,
      pk,
      path,
      value,
      valueType,
    });
    const response = await fetch(`${apiUrl}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const retrievedDoc = await response.json(); // parse JSON body
    //console.log(JSON.stringify(retrievedDoc, null, 2));
    setMotorStep(String(retrievedDoc.arduinoPulseStep));
  }, []);

  const updateCosmosRestDurationValue = useCallback(async (value) => {
    const container = "settings";
    const item_id = "TSDP_T01_UNIT";
    const pk = "TSDP_T01";
    const path = "arduinoRestNextDosing";
    const valueType = "int";

    const apiUrl = "/api/patchItemCosmos";
    const params = new URLSearchParams({
      container,
      item_id,
      pk,
      path,
      value,
      valueType,
    });
    const response = await fetch(`${apiUrl}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const retrievedDoc = await response.json(); // parse JSON body
    //console.log(JSON.stringify(retrievedDoc, null, 2));
    setRestDuration(String(retrievedDoc.arduinoRestNextDosing));
  }, []);

  const updateCosmosFrameIntervalValue = useCallback(async (value) => {
    const container = "settings";
    const item_id = "TSDP_T01_UNIT";
    const pk = "TSDP_T01";
    const path = "processingFrameInterval";
    const valueType = "int";

    const apiUrl = "/api/patchItemCosmos";
    const params = new URLSearchParams({
      container,
      item_id,
      pk,
      path,
      value,
      valueType,
    });
    const response = await fetch(`${apiUrl}?${params.toString()}`);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const retrievedDoc = await response.json(); // parse JSON body
    //console.log(JSON.stringify(retrievedDoc, null, 2));
    setFrameInterval(String(retrievedDoc.processingFrameInterval));
  }, []);

  const validateOperation = useCallback(async () => {
    try {
      const container = "settings";
      const item_id = "TSDP_T01_UNIT";
      const pk = "TSDP_T01";
      const apiUrl = "/api/readItemCosmos";
      const params = new URLSearchParams({ container, item_id, pk });
      const response = await fetch(`${apiUrl}?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const retrievedDoc = await response.json(); // parse JSON body
      setMotorStep(String(retrievedDoc.arduinoPulseStep));
      setRestDuration(String(retrievedDoc.arduinoRestNextDosing));
      setFrameInterval(String(retrievedDoc.processingFrameInterval));
      //console.log(JSON.stringify(retrievedDoc, null, 2));
    } catch (err) {
      console.log(JSON.stringify(err, null, 2));
    }
  }, []);

  useEffect(() => {
    validateOperation();
  }, [validateOperation]);

  const MotorSettingPanel = ({ header }) => (
    <Panel bordered={true} shaded header={header}>
      <Grid fluid>
        <Row
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Col xs={24} md={12}>
            <Text
              size="sm"
              style={{
                textAlign: "left",
                width: "100%",
                color: "black",
              }}
            >
              Motor Step
            </Text>
          </Col>
          <Col xs={24} md={12}>
            <SelectPicker
              block
              data={stepOptions}
              value={motorStep}
              onChange={(value) => {
                setMotorStep(value);
                updateCosmosMotorStepValue(value);
              }}
            />
          </Col>
        </Row>
        <Row
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Col xs={24} md={12}>
            <Text
              size="sm"
              style={{
                textAlign: "left",
                width: "100%",
                color: "black",
              }}
            >
              Rest Until Next Dose
            </Text>
          </Col>
          <Col xs={24} md={12}>
            <SelectPicker
              block
              data={restDurations}
              value={restDuration}
              onChange={(value) => {
                setRestDuration(value);
                updateCosmosRestDurationValue(value);
              }}
            />
          </Col>
        </Row>
      </Grid>
    </Panel>
  );

  const ProcessSettingPanel = ({ header }) => (
    <Panel bordered={true} shaded header={header}>
      <Grid fluid>
        <Row
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
          }}
        >
          <Col xs={24} md={12}>
            <Text
              size="sm"
              style={{
                textAlign: "left",
                width: "100%",
                color: "black",
              }}
            >
              Frame Interval
            </Text>
          </Col>
          <Col xs={24} md={12}>
            <SelectPicker
              block
              data={frameIntervals}
              value={frameInterval}
              onChange={(value) => {
                setFrameInterval(value);
                updateCosmosFrameIntervalValue(value);
              }}
            />
          </Col>
        </Row>
      </Grid>
    </Panel>
  );

  return (
    <>
      <Grid fluid style={{ marginTop: -10 }}>
        {/*<Row
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
          onClick={handleOpenStream1}
        >
          <Button startIcon={<EyeRoundIcon />} appearance="primary">
            Live Stream
          </Button>
        </Row>*/}
        <Divider labelPlacement="start">SSTD Camera 01</Divider>
        <Row style={{ marginBottom: 40 }}>
          <Col xs={24} md={12}>
            <MotorSettingPanel header="Motor Setting" />
          </Col>
          <Col xs={24} md={12}>
            <ProcessSettingPanel header="Frame Processing" />
          </Col>
        </Row>
      </Grid>
    </>
  );
}

export default Settings;
